// Entitlements — the SKU view at /admin/entitlements/skus.
// Lists CATALOG-defined SKUs with current grant counts, lets an admin grant
// to one user via email, or revoke an existing grant. Backed by live API.

import {useEffect, useState} from "react";
import {inject, observer} from "mobx-react";

import {CATALOG} from "../../stores/EntitlementsStore";

const TENANT_WIDE = "*";

const formatDate = (iso) => {
  if (!iso) return "no expiry";
  try { return new Date(iso).toLocaleDateString(undefined, {month: "short", day: "numeric", year: "numeric"}); }
  catch { return iso; }
};

const Entitlements = ({entitlementsStore}) => {
  useEffect(() => { entitlementsStore.load(); }, [entitlementsStore]);

  // Per-SKU inline form state — one open form at a time keeps the UI calm.
  const [openSku, setOpenSku] = useState(null);
  const [email, setEmail] = useState("");
  const [expires, setExpires] = useState("");
  const [cap, setCap] = useState("");
  const [effect, setEffect] = useState("allow");
  const [formErr, setFormErr] = useState(null);

  const openGrantForm = (skuKey) => {
    setOpenSku(skuKey);
    setEmail(""); setExpires(""); setCap(""); setEffect("allow"); setFormErr(null);
  };

  const submitGrant = async (skuKey) => {
    if (!email.trim()) { setFormErr("Email is required"); return; }
    setFormErr(null);
    try {
      await entitlementsStore.grant({
        user_id: email.trim(),
        feature: skuKey,
        expires_at: expires || null,
        cap_usd_monthly: cap ? Number(cap) : null,
        effect,
      });
      setOpenSku(null);
    } catch (e) {
      setFormErr(e.message || String(e));
    }
  };

  const handleRevoke = async (user_id, feature) => {
    if (!confirm(`Revoke ${feature} from ${user_id}?`)) return;
    try { await entitlementsStore.revoke({user_id, feature}); }
    catch (e) { alert(`Revoke failed: ${e.message || e}`); }
  };

  return (
    <>
      <div className="page-hero">
        <div>
          <div className="adminEnt-eyebrow section-eyebrow">Catalog</div>
          <h1 className="serif hero-display">Entitlements</h1>
          <p className="lede">
            Each SKU below is either tenant-wide (everyone has it) or per-user
            (granted individually). Changes propagate in under one minute via
            feature flags.
          </p>
        </div>
        <div className="page-hero-actions">
          <button
            className="btn button-ghost"
            onClick={() => entitlementsStore.load()}
            disabled={entitlementsStore.loading}
          >
            {entitlementsStore.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {entitlementsStore.error && (
        <div className="err">Error: {entitlementsStore.error}</div>
      )}

      {CATALOG.map((sku) => {
        const grants = entitlementsStore.grantsByFeature[sku.key] || [];
        const tenantWide = grants.find(g => g.user_id === TENANT_WIDE);
        const perUser = grants.filter(g => g.user_id !== TENANT_WIDE);
        const isFormOpen = openSku === sku.key;

        return (
          <section key={sku.key} className="adminEnt-skuRow stone-card">
            <div className="sku-head">
              <div>
                <div className="sku-name">{sku.name}</div>
                <div className="sku-desc">{sku.description}</div>
              </div>
              <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                {tenantWide && <span className="chip brand dot">Tenant-wide</span>}
                {sku.key === "plex-cp" && <span className="chip premium">Premium</span>}
                <span className="chip">{perUser.length} per-user</span>
                <button
                  className="btn small primary button-primary"
                  onClick={() => isFormOpen ? setOpenSku(null) : openGrantForm(sku.key)}
                >
                  {isFormOpen ? "Cancel" : "+ Grant"}
                </button>
              </div>
            </div>

            {isFormOpen && (
              <div className="stone-card--subtle" style={{
                padding: 16, marginBottom: 14,
                display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 10,
                alignItems: "end",
              }}>
                <label style={{display: "flex", flexDirection: "column", gap: 4}}>
                  <span style={{fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em"}}>
                    User email (or <code>*</code> for tenant-wide)
                  </span>
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@yourcompany.com"
                    style={{height: 32, padding: "0 10px", borderRadius: 6, border: "1px solid var(--line)", fontFamily: "inherit"}}
                  />
                </label>
                <label style={{display: "flex", flexDirection: "column", gap: 4}}>
                  <span style={{fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em"}}>
                    Expires (optional)
                  </span>
                  <input
                    type="date"
                    value={expires}
                    onChange={(e) => setExpires(e.target.value)}
                    style={{height: 32, padding: "0 10px", borderRadius: 6, border: "1px solid var(--line)", fontFamily: "inherit"}}
                  />
                </label>
                <label style={{display: "flex", flexDirection: "column", gap: 4}}>
                  <span style={{fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em"}}>
                    Cap $/mo (optional)
                  </span>
                  <input
                    type="number"
                    min="0" step="10"
                    value={cap}
                    onChange={(e) => setCap(e.target.value)}
                    placeholder="—"
                    style={{height: 32, padding: "0 10px", borderRadius: 6, border: "1px solid var(--line)", fontFamily: "inherit"}}
                  />
                </label>
                <label style={{display: "flex", flexDirection: "column", gap: 4}}>
                  <span style={{fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em"}}>
                    Effect
                  </span>
                  <select
                    value={effect}
                    onChange={(e) => setEffect(e.target.value)}
                    style={{height: 32, padding: "0 10px", borderRadius: 6, border: "1px solid var(--line)", fontFamily: "inherit"}}
                  >
                    <option value="allow">Allow</option>
                    <option value="deny">Deny</option>
                  </select>
                </label>
                <button
                  className="btn primary button-primary"
                  onClick={() => submitGrant(sku.key)}
                  disabled={entitlementsStore.isPending(email.trim(), sku.key)}
                >
                  Grant
                </button>
                {formErr && (
                  <div className="err" style={{gridColumn: "1 / -1", marginBottom: 0}}>
                    {formErr}
                  </div>
                )}
              </div>
            )}

            {grants.length === 0 ? (
              <div style={{color: "var(--muted)", fontSize: 13, paddingLeft: 2}}>
                No grants yet — click + Grant to add the first one.
              </div>
            ) : (
              <table style={{width: "100%", borderCollapse: "collapse", fontSize: 13}}>
                <thead>
                  <tr style={{textAlign: "left", color: "var(--muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em"}}>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)"}}>User</th>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)"}}>Effect</th>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)"}}>Granted by</th>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)"}}>Expires</th>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)"}}>Cap</th>
                    <th style={{padding: "8px 6px", borderBottom: "1px solid var(--line)", textAlign: "right"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map((g) => (
                    <tr key={`${g.user_id}#${g.feature}`}>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)"}}>
                        {g.user_id === TENANT_WIDE
                          ? <span className="chip brand">Everyone in tenant</span>
                          : g.user_id}
                      </td>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)"}}>
                        {g.effect === "deny"
                          ? <span className="chip" style={{color: "var(--danger, #b42318)"}}>Deny</span>
                          : <span className="chip">Allow</span>}
                      </td>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)"}}>
                        {g.granted_by || "—"}
                      </td>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)"}}>
                        {formatDate(g.expires_at)}
                      </td>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)"}}>
                        {g.cap_usd_monthly != null ? `$${g.cap_usd_monthly}/mo` : "—"}
                      </td>
                      <td style={{padding: "10px 6px", borderBottom: "1px solid var(--line-2)", textAlign: "right"}}>
                        <button
                          className="btn small ghost button-ghost"
                          onClick={() => handleRevoke(g.user_id, g.feature)}
                          disabled={entitlementsStore.isPending(g.user_id, g.feature)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </>
  );
};

export default inject("entitlementsStore")(observer(Entitlements));
